import { FabricObject, FixedLayout, LayoutManager } from "fabric";
import { FontLoader, ImageLoader } from "../../helpers";
import { FabricIText, FabricPattern } from "../../objects";
import {
  WarpedTextLayerProps,
  WarpedTextProps,
} from "./interfaces";
import {
  LayerCustomType,
  TextCaseType,
  WarpPresetParams,
  WarpStyle,
  WarpTransformOptions,
} from "../../interfaces";
import {
  getDefaultTransformOptions,
  parseTransformOptions,
  pickProps,
} from "../../utils";
import {
  drawWarpControls,
  setupWarpPointControls,
  setWarpPointEditMode,
} from "./controls";
import { WarpedTextRenderer } from "./renderer";

const DEFAULT_FONT_SRC = "/fonts/default_font.ttf";

export default class WarpedTextLayer extends WarpedTextRenderer {
  protected static customType: LayerCustomType = LayerCustomType.WarpedText;
  protected static defaultTypeProps: Partial<WarpedTextProps> = {
    warpStyle: WarpStyle.Arch,
    fill: "#000000",
    fontFamily: "Times New Roman",
    maxFontSize: 200,
    paintFirst: "stroke",
    stroke: "#000000",
    strokeWidth: 0,
    strokeLineJoin: "round",
    textAlign: "center",
    prefix: "",
    infix: "A",
    suffix: "",
    textCase: TextCaseType.None,
    warpPresetParams: { bend: 1 },
    patternScale: 1,
  };

  name: string = "Warped Text";
  warpPointEditMode: boolean = false;

  constructor(options: Partial<WarpedTextLayerProps> = {}) {
    const {
      transformSettings = {},
      typeSettings = {},
      ...layerProps
    } = options;
    super();

    // Prevent LayoutManager from repositioning the group when glyphs are added/removed
    this.layoutManager = new LayoutManager(new FixedLayout());

    const parsedTransformOptions = parseTransformOptions(
      typeSettings.transformOptions,
    );
    const textOptions = {
      ...WarpedTextLayer.defaultTypeProps,
      ...typeSettings,
      ...(parsedTransformOptions ? { transformOptions: parsedTransformOptions } : {}),
    };
    this.textObject = new FabricIText("", textOptions);
    // textObject is property storage only — not added to group

    this.set(transformSettings);
    this.set({ ...layerProps, objectCaching: false });

    if (!parseTransformOptions(this.textObject.transformOptions)) {
      this.textObject.set({
        transformOptions: getDefaultTransformOptions(
          this.textObject.warpStyle,
          this.textObject.warpPresetParams ?? undefined,
        ),
      });
    }

    setupWarpPointControls(this);
    setWarpPointEditMode(this, false);
    this.loadFont(this.textObject.fontSrc);
    this.loadPattern(
      typeSettings.patternImageSrc || (typeSettings.fill as string),
    );
  }

  // Preserve borderObject when clearing glyph paths
  protected override getGlyphsToRemove(): FabricObject[] {
    return this._objects.filter((obj) => obj !== this.borderObject);
  }

  protected override rerender(): void {
    super.rerender();
    this.updateText();
    this.renderGlyphs();
    this.canvas?.requestRenderAll();
  }

  override getDefaultDimensions(size: number) {
    return { width: size / 2, height: size / 5 };
  }

  getWarpTransformOptions(): WarpTransformOptions | null {
    return parseTransformOptions(this.textObject.transformOptions);
  }

  updateWarpTransformOptions(options: WarpTransformOptions): void {
    // Dragging a handle means the user has gone off-preset → clear warpPresetParams
    this.setTextProps({ transformOptions: options, warpPresetParams: null });
  }

  setWarpPointEditMode(mode: boolean): void {
    setWarpPointEditMode(this, mode);
  }

  override drawControls(ctx: CanvasRenderingContext2D, styleOverride = {}): void {
    super.drawControls(ctx, styleOverride);
    drawWarpControls(this, ctx);
  }

  setWarpStyle(style: WarpStyle): void {
    const params: WarpPresetParams = { bend: 1 };
    this.textObject.set({
      warpStyle: style,
      warpPresetParams: params,
      transformOptions: getDefaultTransformOptions(style, params),
    });
    this.rerender();
  }

  setPresetParams(params: WarpPresetParams): void {
    this.textObject.set({
      warpPresetParams: params,
      transformOptions: getDefaultTransformOptions(
        this.textObject.warpStyle,
        params,
      ),
    });
    this.rerender();
  }

  toObject() {
    const object = super.toObject();
    const textProps = this.getTextProps();
    return { ...object, typeSettings: textProps };
  }

  getTextProps(key?: string) {
    // Warped Text has no single fabric.Text object to hold a Pattern fill
    // (each glyph is its own fabric.Path) — surface the pattern state here so
    // UI checks like `getTextProps("fill") instanceof FabricPattern` behave
    // the same as TextLayer/CurvedTextLayer.
    if (key === "fill") {
      return this.patternObject ?? this.textObject.get("fill");
    }
    if (key) {
      return this.textObject.get(key);
    }
    return pickProps(this.textObject, [
      "prefix",
      "infix",
      "suffix",
      "fill",
      "fontPath",
      "fontSrc",
      "fontFamily",
      "fontSize",
      "maxFontSize",
      "textAlign",
      "textCase",
      "charSpacing",
      "lineHeight",
      "stroke",
      "strokeWidth",
      "warpStyle",
      "transformOptions",
      "warpPresetParams",
      "globalCompositeOperation",
      "patternImagePath",
      "patternScale",
      "patternFit",
    ]);
  }

  async loadPattern(src?: string | Blob): Promise<string | undefined> {
    if (!src) {
      this.patternObject = undefined;
      this.rerender();
      return;
    }

    const srcToLoad = src instanceof Blob ? URL.createObjectURL(src) : src;

    if (srcToLoad.startsWith("#")) {
      this.patternObject = undefined;
      this.rerender();
      return;
    }

    try {
      const imageElement = await ImageLoader.load(srcToLoad);
      if (!imageElement) return "";

      if (this.patternObject) {
        this.patternObject.source = imageElement;
      } else {
        this.patternObject = new FabricPattern({
          source: imageElement,
          crossOrigin: "anonymous",
          repeat: "repeat",
        });
      }

      this.rerender();
      return srcToLoad;
    } catch (error) {
      console.error(error);
      return "";
    }
  }

  async loadFont(fontSrc?: string): Promise<void> {
    this.textObject.set({ fontSrc });

    const fontFace = fontSrc
      ? await FontLoader.loadFontFace(fontSrc)
      : undefined;
    this.textObject.set({
      fontFamily: fontFace?.family || WarpedTextLayer.defaultTypeProps.fontFamily,
    });

    try {
      this._opentypeFont = await FontLoader.loadOpenTypeFont(
        fontSrc || DEFAULT_FONT_SRC,
      );
    } catch {
    }
    if (!this._opentypeFont) {
      this._opentypeFont = await FontLoader.loadOpenTypeFont(DEFAULT_FONT_SRC);
    }

    this.rerender();
  }
}

export * from "./interfaces";
